import { TaskLabel } from '@seen/core/models/taskLabel/taskLabel'

import { Button, type ButtonProps, cn } from '@seen/ui'
import { type ReactNode, type Ref, useMemo } from 'react'
import type { Field } from '#src/components/core/form/FormField.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { LabelCircles } from '#src/components/LabelCircles.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { TaskLabelCombobox } from '#src/components/TaskLabelCombobox.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

type Base = Field<string[]> & Omit<ButtonProps, 'value' | 'onChange'>
export interface TaskLabelComboboxButtonProps extends Base {
  ref?: Ref<HTMLButtonElement>
}

export function TaskLabelComboboxButton({
  value: valueMaybeIncludingDeleted,
  onChange,
  className,
  ref,
  ...rest
}: TaskLabelComboboxButtonProps): ReactNode {
  const { data: labels } = useSearch(TaskLabel, TaskLabel.baseSearchQuery())

  const valueWithoutDeleted = useMemo(
    () =>
      valueMaybeIncludingDeleted.filter(
        (v) => !!labels?.find((l) => l.reference.reference === v)
      ),
    [valueMaybeIncludingDeleted, labels]
  )

  return (
    <TaskLabelCombobox value={valueMaybeIncludingDeleted} onChange={onChange}>
      <Button
        variant="outline"
        className={cn('flex items-center gap-2 shadow-sm', className)}
        size={valueWithoutDeleted.length ? undefined : 'icon'}
        ref={ref}
        {...rest}
      >
        {!valueWithoutDeleted.length && <IconLabel />}
        {valueWithoutDeleted.length === 1 && (
          <div className="flex items-center gap-1">
            <LabelColor
              color={
                labels?.find(
                  (l) => l.reference.reference === valueWithoutDeleted[0]
                )?.color
              }
            />
            {
              labels?.find(
                (l) => l.reference.reference === valueWithoutDeleted[0]
              )?.name
            }
          </div>
        )}
        {valueWithoutDeleted.length > 1 && (
          <div className="flex items-center gap-1">
            <LabelCircles
              labels={
                labels
                  ?.filter((l) =>
                    valueWithoutDeleted.includes(l.reference.reference)
                  )
                  .map((l) => l.reference.reference) ?? []
              }
            />
            {valueWithoutDeleted.length} labels
          </div>
        )}
      </Button>
    </TaskLabelCombobox>
  )
}
